import {Props, VariantItemProps} from "./types";
import classNames from "classnames";
import { useState, useEffect, useContext, useMemo } from "react";
import SwiperCore, { Navigation, Pagination, Mousewheel} from "swiper";
import dataContainer from "../../services/DataContainer";
import { DataContainerContext } from "../../context/DataContainerContext";
import { MainButtons } from "../../enum/enums";
import { Button } from "../Button/Button";
import { VarianButtonEnum } from "../../enum/Button/variantButton.model";
import { textButton } from "../../enum/Button/textButton.model";
import { IconModelEnum } from "../../enum/Button/iconButton.model";
import { SearchInput } from "../Search-Input/SearchInput";
import useShowExterior360 from "../../utils/useShowExterior360";
import { handleKeyboardNavigation, announceToScreenReader } from "../../utils/accessibility";
import { AdobeEventType } from "../../enum/adobe/adobeEventType";
import { AdobeEventActions } from "../../enum/adobe/adobeEventActions";
import { mapToAdobeComponentStructure } from "../../enum/adobe/subStepNames";
import { useDebounce } from "../../utils/useDebounce";

SwiperCore.use([Navigation, Pagination, Mousewheel]);

interface ActiveColorGroup {
  name: string;
  id: number;
  index: number;
}
interface ActiveColorHue {
  name: string;
  id: number;
  group_id: number;
  index: number;
}

interface ColorObject {
  chosenColor: string;
  colorComponent: string;
  colorComponentGroup: string
}

export const VariantItemSwiperColors = ({refreshTruckImage, expandedColorizer, toggleColorizer, isMobile}: Props) => {
  const {
    activeComponent,
    data,
    defaultVariantColor,
    isColorComingFromURL,
    activeComponentGroups,
  } = useContext(DataContainerContext);
  const exterior360 = useShowExterior360();
  const [variantState, setVariantState] = useState<any[] | never>([]);
  const [variantKey, setVariantKey] = useState<any>();
  const modelName = dataContainer.getActualModelName();
  const [preventFromSelectingDefault, setPreventFromSelectingDefault] = useState(true);
  const [colorObj, setColorObj] = useState<ColorObject>({chosenColor: "", colorComponent: "", colorComponentGroup: ""});
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [activeColorGroup, setActiveColorGroup] = useState<ActiveColorGroup>({ name: "", id: 0, index: 0 });
  const [activeColorHue, setActiveColorHue] = useState<ActiveColorHue>({ name: "", id:0, group_id: 0, index: 0 });
  const [searchOpen, setSearchOpen] = useState(false);
  const [colorsArray, setColorsArray] = useState<any[] | undefined>([]);
  const [focusedColorIndex, setFocusedColorIndex] = useState(-1);
  const colorizerProduct = data.find((prd: { id: number; }) => prd.id == 181);

  const colorGroups = colorizerProduct?.component_groups;
  const colorHues = colorGroups?.find((cg: any) => cg.id === activeColorGroup.id)?.components;
  const allColors = useMemo(() => colorGroups?.flatMap((cg: any) => cg?.components.flatMap((ch: any) => ch.available_variations)) ?? [], [colorizerProduct]);

  const handleColorGroupClick = (colorGroup: ActiveColorGroup) => {
    setActiveColorGroup(colorGroup);
    setSearchTerm("");
  };

  const handleColorHueClick = (colorHue: ActiveColorHue) => {
    setActiveColorHue(colorHue);
    setSearchTerm("");
  };

  //Filtering color selection list based on NOT being default and if being disabled
  function filterColorsInColorizer(colors?: any[]){
    if (!colors) return [];
    const allProducts = data
      .map((product) => product.product_info?.description)
      .filter((description): description is string => Boolean(description));

    const filteredByModelAndSearchCode = colors.filter((color: any) => {
      if (color.attribute_values.length > 0){
        const hasSearchcodes = color.attribute_values.some(
          (attribute: any) => attribute.attribute_name === "searchcodes"
        );
        
        if(hasSearchcodes) {
          return color.attribute_values.some(
            (attribute: any) => attribute.value !== modelName
          );
        } else {
          if (color.attribute_values.some((attribute: any) => attribute.value === modelName)){
            return true;
          } else if (!color.attribute_values.some((attribute: any) => allProducts.includes(attribute.value))) {
            return true;
          } else {
            return false;
          }
        }
      }
      return true;
    });

    // Remove disabled colors
    return filteredByModelAndSearchCode.filter((color: any) => {
      if (color.attribute_values.length > 0){
        const isDisabled = color.attribute_values.some((attribute: any) =>
          attribute.value === "Disabled" && attribute.attribute_name.toLowerCase() === modelName
        );
        if (isDisabled) {
          return false;
        }
        return true;
      }
      return true;
    });
  }

  useEffect(() => {
    if (colorGroups) {
      const defaultColorGroup = colorGroups.find((cg: any) => cg?.components.find((ch: any) => ch.available_variations.some((variation: { id: any; }) => dataContainer.isVariationSelected(variation.id))));
      const defaultColorHue = dataContainer.findComponentById(dataContainer.getSelectedColorFromColorizer()?.component_id);
      if (activeColorGroup.name === "") {
        setActiveColorGroup({
          name: defaultColorGroup?.name || "",
          id: defaultColorGroup?.id || 0,
          index: 0,
        });
      }
      if(colorHues) {
        if (activeColorHue.name === "") {
          setActiveColorHue({
            name: defaultColorHue?.name || "",
            id: defaultColorHue?.id || 0,
            group_id: defaultColorHue?.group_id || 0,
            index: 0,
          });
        } else if (activeColorHue.name === "" || (activeColorHue.group_id !== activeColorGroup.id)) {
          setActiveColorHue(((element) => ({
            name: element?.name || element?.key || "",
            id: element?.id || 0,
            group_id: element?.group_id || 0,
            index: 0,
          }))(
            colorGroups.find((cg: any) => cg.id === activeColorGroup.id)?.components?.[0]
          ));
        }
        // Only set colorsArray from colorHues if there's no active search
        if (!debouncedSearchTerm) {
          const currentHueColors = colorHues.find((a:any) => a.id === activeColorHue.id)?.available_variations;
          const filteredDisabledColors = filterColorsInColorizer(currentHueColors);
          setColorsArray(filteredDisabledColors);
        }
      }
    }

    if(debouncedSearchTerm) {
      const lowerCaseSearchTerm = debouncedSearchTerm.toLowerCase();
      const colors = filterColorsInColorizer(allColors);
      setColorsArray(
        colors?.filter((item: any) => {
          const searchcodes = item.attribute_values?.find(
            (attribute: any) => attribute.attribute_name.toLowerCase() === "searchcodes"
          );

          const matchesSearchcodes = searchcodes?.value.toLowerCase().includes(lowerCaseSearchTerm);
          const matchesKey = item.key?.toLowerCase().includes(lowerCaseSearchTerm);

          return matchesSearchcodes || matchesKey;
        }) || []
      );
    }
  },[activeColorGroup, colorGroups, activeColorHue, colorHues, debouncedSearchTerm]);


  useEffect(() => {
    if (colorsArray?.length) {
      const currentSegment = localStorage.getItem("segment");
      const sectionKey =
        `${modelName}_${currentSegment}_DISABLED_OPTIONS`.toLocaleLowerCase();
      const variationsToSkip = dataContainer
        .sectionKey("Segment.Settings", sectionKey)
        .split(",").map((s: string) => s.trim());

      const filteredVariations = colorsArray.filter((variation: any) => {
        if(variationsToSkip.includes(variation.original_key)){
          return false;
        }
        else if(variation.dependencies?.length > 0){
          const chosenVariationBasedOnDependencyOfParentVariation = variation.dependencies
            .find((dependency: any) => dataContainer.isVariationSelected(dependency.depends_on_variation_id));
          return chosenVariationBasedOnDependencyOfParentVariation ? true : false;
        }
        return true;
      });

      const colors = filterColorsInColorizer(allColors);

      const containsDefaultSelection = colors?.some((color: VariantItemProps) => color.attribute_values.length && color.attribute_values.find((attribute) => attribute.value === modelName));
      if (!containsDefaultSelection && !isColorComingFromURL) {
        dataContainer.toggleVariationSelection(colors[0]?.id);
      }
      else if (isColorComingFromURL && preventFromSelectingDefault) {
        const isColorValid = colors.find((color: VariantItemProps) => color.key === defaultVariantColor);
        if (!isColorValid) {
          dataContainer.toggleVariationSelection(colors[0]?.id);
        }
        setPreventFromSelectingDefault(false);
      }
      setVariantState(filteredVariations);
    } else if (debouncedSearchTerm && colorsArray?.length === 0) {
      // When search term exists but no results found
      setVariantState([]);
    }
  }, [colorsArray, debouncedSearchTerm]);


  const triggerVariationClick = (variant: VariantItemProps) => {
    !dataContainer.isVariationSelected(variant.id) && dataContainer.toggleVariationSelection(variant.id);
    refreshTruckImage();
    announceToScreenReader(`Selected color: ${variant.key}`);
  };

  const handleColorKeyboardNavigation = (e: React.KeyboardEvent) => {
    // Skip keyboard navigation on mobile devices
    if (isMobile) return;
    
    const totalColors = variantState.length;
    if (totalColors === 0) return;

    handleKeyboardNavigation(e,
      () => {
        // Handle Enter/Space activation
        if (variantState[focusedColorIndex]) {
          setVariantKey(variantState[focusedColorIndex].key);
          triggerVariationClick(variantState[focusedColorIndex]);
          dataContainer.sendDataToAdobeDataLayer(
            AdobeEventType.APPLICATION_INTERACTION, 
            activeComponentGroups.index + 3, 
            activeComponentGroups.name.replaceAll("_", " "), 
            mapToAdobeComponentStructure[activeComponent.name as keyof typeof mapToAdobeComponentStructure],
            AdobeEventActions.OPTION_EVALUATED,
            getSelectedOptions(variantState[focusedColorIndex]),
            ""
          );
        }
      },
      () => {
        // Handle next (ArrowRight)
        setFocusedColorIndex((prev) => (prev + 1) % totalColors);
      },
      () => {
        // Handle previous (ArrowLeft)
        setFocusedColorIndex((prev) => (prev - 1 + totalColors) % totalColors);
      }
    );
  };

  useEffect(() => {
    if(allColors.length) {
      const colorVariation = allColors.find((variation: VariantItemProps) => dataContainer.isVariationSelected(variation.id));
      const colorComponent = dataContainer.findComponentById(colorVariation?.component_id);
      const colorComponentGroup = dataContainer.findComponentGroupByID(colorComponent?.group_id);
      setColorObj({
        chosenColor: colorVariation?.key,
        colorComponent: colorComponent?.name || colorComponent?.key,
        colorComponentGroup: colorComponentGroup?.name_localized || colorComponentGroup?.name
      });
    }
  }, [variantKey]);

  const getSelectedOptions = (selectedColor: any) => {
    const colorFamily = dataContainer.findComponentById(selectedColor.component_id);
    const colorGroup = dataContainer.findComponentGroupByID(colorFamily.group_id);

    return {
      "Cab Color": selectedColor.key,
      "Search Used": searchTerm !== "" ? "Yes" : "No",
      "Search Keyword": searchTerm,
      "Color Group": colorGroup.name,
      "Color Family": colorFamily.name,
    };
  };


  return (
    <>
      <div
        className={classNames(
          "relative transition-all transform border-t duration-300 ease-in-out bottom-0",
          {"h-32 max-h-32": !isMobile && !expandedColorizer},
          {"h-[280px] max-h-[280px]": !isMobile && expandedColorizer},
          {"h-56 max-h-56": !expandedColorizer && isMobile},
          {"max-h-[100vh]" : (expandedColorizer && isMobile)}
        )}
      >
        <div className="h-[52px] flex justify-between items-center">
          <div className="relative left-6">
            <Button
              variant={VarianButtonEnum.ICONBUTTON}
              onClickEvent={() => {
                const nextState = !expandedColorizer;

                dataContainer.sendDataToAdobeDataLayer(
                  AdobeEventType.APPLICATION_INTERACTION, 
                  activeComponentGroups.index + 3, 
                  activeComponentGroups.name.replaceAll("_", " "), 
                  mapToAdobeComponentStructure[activeComponent.name as keyof typeof mapToAdobeComponentStructure],
                  nextState ? AdobeEventActions.SHOW_MORE : AdobeEventActions.SHOW_LESS,
                  {},
                  ""
                );
                toggleColorizer(nextState);
              }}
              text={!isMobile ? (!expandedColorizer ? dataContainer.sectionKey(MainButtons.MAIN_BUTTONS, MainButtons.SHOW_MORE_BUTTON) : dataContainer.sectionKey(MainButtons.MAIN_BUTTONS, MainButtons.SHOW_LESS_BUTTON)) : ""}
              customClassNames="bg-light-grey-light hover:bg-light-grey-hover text-xs h-10 w-10 sm:w-32"
              textColor={textButton.DARK_GREY_LIGHT}
              icon={!expandedColorizer ? IconModelEnum.ShowMoreButton : IconModelEnum.ShowLessButton}
            />
          </div>
          <div className={classNames(
            "text-center relative md:left-[70px] flex flex-row py-3 gap-1 text-xs",
            {"flex-col": isMobile},
            {"hidden": (isMobile && searchOpen)}
          )}>
            <div>
              <span className="uppercase">{colorObj?.colorComponentGroup} </span>
              <span className="uppercase text-font-vonum-bold">{colorObj?.chosenColor} </span>
            </div>
          </div>
          <SearchInput
            value={searchTerm}
            onSearch={(e) => setSearchTerm(e.target.value)}
            customClassNames="transition-all transform duration-300 ease-in-out bg-light-grey-light h-10 rounded-full"
            isMobile={isMobile}
            searchOpen={searchOpen}
            setSearchOpen={setSearchOpen}
          />
        </div>
        <div className={`transition-all transform duration-300 ease-in-out border-t ${!expandedColorizer && "!h-0 overflow-hidden"} border-t-gray-lighter h-[180px] md:h-28 flex flex-col justify-center items-center`}>
          <div className="flex justify-center gap-8 h-full w-1/3">
            {colorGroups?.map((cg: any, idx: number) => (
              <div
                key={idx}
                className={classNames(
                  {"border-b transform duration-100 ease-in border-black": activeColorGroup?.id === cg.id},
                  "max-h-3/4 self-center gap-2 flex justify-between transform duration-200 ease-in")}
              >
                <Button
                  variant={VarianButtonEnum.TEXTBUTTON}
                  text={ cg?.name_localized || cg?.name }
                  customClassNames="!p-0 h-5"
                  onClickEvent={() => handleColorGroupClick({name: cg?.name, id: cg.id, index: idx})}
                />
              </div>
            ))
            }
          </div>
          {(isMobile && expandedColorizer) && <div className="bg-gray-lighter h-0.5 w-full"></div>}
          <div className="flex flex-wrap justify-center gap-x-10 md:gap-x-8 gap-y-5 h-full w-full py-6 ">
            {colorHues?.map((ch: any, idx: number) => (
              <div
                key={idx}
                className={classNames(
                  {"border-b border-black transform duration-100 ease-in": activeColorHue?.id === ch.id},
                  "max-h-3/4 self-center flex justify-between transform duration-200 ease-in")}
              >
                <Button
                  variant={VarianButtonEnum.TEXTBUTTON}
                  text={ ch?.name || ch?.key }
                  customClassNames="!p-0 h-5"
                  onClickEvent={() => handleColorHueClick({name: ch?.key, id: ch.id, group_id: ch.group_id, index: idx})}
                />
              </div>
            ))
            }
          </div>
        </div>
        <div
          className={classNames(
            "border border-t overflow-y-auto overflow-x-hidden h-full border-gray-lighter transition-all transform duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500",
            {"max-h-[73px]": !expandedColorizer && !isMobile},
            {"max-h-28": expandedColorizer && !isMobile},
            {"max-h-[160px]": !expandedColorizer && isMobile},
            {"max-h-[65vh]": expandedColorizer && isMobile},
          )}
          tabIndex={0}
          role="region"
          aria-label="Color options - scrollable area"
          onKeyDown={handleColorKeyboardNavigation}
        >
          {debouncedSearchTerm && variantState?.length === 0 ? (
            <div className="flex justify-center items-center h-full w-full">
              <p className="text-center text-gray-600">
                {dataContainer.sectionKey("Main.Texts", "search_color_not_found")}
              </p>
            </div>
          ) : (
            <div dir="rtl" className="relative overflow-x-hidden bg-white" id="color-scrollbar">
              <div dir="ltr" className="grid xl:grid-cols-30 lg:grid-cols-20 md:grid-cols-12 sm:grid-cols-8 grid-cols-8">
                {variantState?.map((variant: VariantItemProps, idx: number) => (
                  <div
                    className="h-12 w-auto border-r border-b flex justify-center items-center py-0 border-gray-lighter"
                    title={variant.key}
                    id={"color-variation"}
                    key={idx}
                    aria-selected={dataContainer?.isVariationSelected(variant.id)}
                    aria-label={`Color option: ${variant.key}`}
                    onClick={() => {
                      setVariantKey(variant.key);
                      triggerVariationClick(variant);
                      dataContainer.sendDataToAdobeDataLayer(
                        AdobeEventType.APPLICATION_INTERACTION, 
                        activeComponentGroups.index + 3, 
                        activeComponentGroups.name.replaceAll("_", " "), 
                        mapToAdobeComponentStructure[activeComponent.name as keyof typeof mapToAdobeComponentStructure],
                        AdobeEventActions.OPTION_EVALUATED,
                        getSelectedOptions(variant)
                      );

                    }}>
                    <div className={classNames("flex w-8 h-8 hover:cursor-pointer items-center py-0 justify-center", variant.className)}>
                      <div style={dataContainer?.isVariationSelected(variant.id) ? { backgroundColor: `#${variant.original_key.slice(2)}` } : {backgroundColor: "transparent"}}
                        className={classNames("rounded-full p-px", variant.className)}>
                        <img
                          style={{
                            backgroundColor: `#${variant.original_key.slice(2)}`,
                          }}
                          src={variant?.assets[0]?.preview_image_url}
                          alt={variant.key}
                          className={"object-contain border-2 border-white rounded-full"}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>)}
        </div>
      </div>
      {!exterior360 && <div className="h-7 fixed bottom-0 left-0 w-full pointer-events-none bg-gradient-to-b from-transparent to-white hover:cursor-pointer"></div>}
    </>
  );
};
